import {
  COLD_HOT_COPY,
  LOWEST_HIGHEST_RANGE_COPY,
  OLDEST_NEWEST_COPY,
  SortingDirectionValues,
} from 'models/Sort';
import {
  DATA_ROOM_MEMBER_STATUS,
  DataRoomMember,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import { isDateFromLastDays, isDateToday, isDateYesterday } from 'dateUtils';
import { ObjectValues } from 'types';

// The table puts each column key straight onto the `td` as a class, so keys
// must not collide with global component classes — `.questions` is declared
// unscoped by the Q&A page and would force `flex-direction: column` on the
// cell. Hence the `Column` suffix, same as `chatColumn`.
export type MemberTableColumn =
  | 'name'
  | 'group'
  | 'files'
  | 'qa'
  | 'status'
  | 'views'
  | 'questionsColumn'
  | 'downloads'
  | 'lastSeen'
  | 'teams'
  | 'chatColumn'
  | 'actions';

export type MemberRow = DataRoomMember &
  Partial<Record<MemberTableColumn, unknown>>;

export const HEADING_TRANSLATIONS: Record<MemberTableColumn, string> = {
  name: 'name',
  group: 'group',
  files: 'files',
  qa: 'q&a',
  status: 'status',
  views: 'views',
  questionsColumn: 'questions',
  downloads: 'downloads',
  lastSeen: 'lastSeen',
  teams: 'role',
  chatColumn: 'chat',
  actions: '',
};

// Every column is sortable on the FE except the role column (a member can hold
// several, so an order over them means little) and the chat/actions buttons.
export const SORTING_VALUES: Partial<Record<MemberTableColumn, string>> = {
  name: 'name',
  group: 'group',
  files: 'files',
  qa: 'qa',
  status: 'status',
  views: 'views',
  // Sort field stays `questions` so the URL and sortValueGetters keys are
  // unaffected by the column key rename.
  questionsColumn: 'questions',
  downloads: 'downloads',
  lastSeen: 'lastSeen',
};

export const CUSTOM_SIZES: Partial<Record<MemberTableColumn, number>> = {
  name: 340,
  group: 200,
  files: 100,
  qa: 100,
  status: 110,
  views: 100,
  questionsColumn: 110,
  downloads: 120,
  lastSeen: 130,
  teams: 120,
  chatColumn: 60,
  actions: 60,
};

// Hot members sort above cold ones, and members the BE has no status for last.
export const STATUS_SORT_RANK: Record<string, number> = {
  [DATA_ROOM_MEMBER_STATUS.HOT]: 2,
  [DATA_ROOM_MEMBER_STATUS.COLD]: 1,
};

// Buy-side sorting lives in the URL so it can be shared and survives a reload.
// Sell-side keeps its own local sort, hence a dedicated param rather than `sort`.
export const BUY_SIDE_SORT_PARAM = 'buySideSort';

export const DEFAULT_MEMBERS_SORT = '-name';

// Both member tables are paginated on the FE and sit on the same page, so each
// gets its own param rather than sharing `page`.
export const BUY_SIDE_PAGE_PARAM = 'buySidePage';
export const SELL_SIDE_PAGE_PARAM = 'sellSidePage';

export const MEMBERS_TABLE_LIMIT = 10;

export const BUY_SIDE_SORT_COLUMNS: MemberTableColumn[] = [
  'name',
  'group',
  'files',
  'qa',
  'status',
  'views',
  'questionsColumn',
  'downloads',
  'lastSeen',
];

// The sorter modal spells the role columns out; the table headers stay short.
export const MULTI_SORT_LABELS: Partial<Record<MemberTableColumn, string>> = {
  ...HEADING_TRANSLATIONS,
  files: 'filesRole',
  qa: 'qaRole',
};

// The manage-columns drawer spells every column out, including the ones with
// an icon-only header, and reuses the sorter's longer role labels.
export const MANAGE_COLUMNS_LABELS: Record<MemberTableColumn, string> = {
  ...HEADING_TRANSLATIONS,
  ...MULTI_SORT_LABELS,
  actions: 'actions',
};

// The name column identifies the row, so it stays checked (and disabled).
export const REQUIRED_COLUMN: MemberTableColumn = 'name';

// The role column is opt-in: it starts out hidden for everyone and only shows
// up once a user checks it in the drawer. Applies until the user saves the
// table's settings, after which their own choice is the only thing that counts.
export const DEFAULT_HIDDEN_COLUMNS: MemberTableColumn[] = ['teams'];

// Keyed by sort field (identical to the column key here). Fields left out fall
// back to A-Z / Z-A inside useMultiSort.
export const MULTI_SORT_DIRECTION_COPY: Record<
  string,
  SortingDirectionValues[string]
> = {
  status: COLD_HOT_COPY,
  views: LOWEST_HIGHEST_RANGE_COPY,
  questions: LOWEST_HIGHEST_RANGE_COPY,
  downloads: LOWEST_HIGHEST_RANGE_COPY,
  lastSeen: OLDEST_NEWEST_COPY,
};

// Filters are applied on the FE too, so these params only ever drive the
// in-memory filtering in useBuySideMemberFilters.
export const BUY_SIDE_FILTER_PARAMS = {
  NAME: 'buySideName',
  GROUP: 'buySideGroup',
  FILES_ROLE: 'buySideFilesRole',
  QA_ROLE: 'buySideQaRole',
  STATUS: 'buySideStatus',
  LAST_SEEN: 'buySideLastSeen',
  WITHDRAWN: 'buySideWithdrawn',
} as const;

export const LAST_SEEN_FILTER = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  LAST_7_DAYS: '7days',
  LAST_30_DAYS: '30days',
  LAST_60_DAYS: '60days',
  ALL_TIME: 'allTime',
} as const;

export type LastSeenFilter = ObjectValues<typeof LAST_SEEN_FILTER>;

export const WITHDRAWN_FILTER = {
  INCLUDE: 'include',
  EXCLUDE: 'exclude',
} as const;

// "All time" is deliberately absent: no predicate means no filtering, which is
// exactly what "same as if the filter is not applied" asks for.
export const LAST_SEEN_PREDICATES: Partial<
  Record<LastSeenFilter, (date: Date | string) => boolean>
> = {
  [LAST_SEEN_FILTER.TODAY]: isDateToday,
  [LAST_SEEN_FILTER.YESTERDAY]: isDateYesterday,
  [LAST_SEEN_FILTER.LAST_7_DAYS]: (date) => isDateFromLastDays(date, 7),
  [LAST_SEEN_FILTER.LAST_30_DAYS]: (date) => isDateFromLastDays(date, 30),
  [LAST_SEEN_FILTER.LAST_60_DAYS]: (date) => isDateFromLastDays(date, 60),
};

export const LAST_SEEN_FILTER_COPY: Record<LastSeenFilter, string> = {
  [LAST_SEEN_FILTER.TODAY]: 'today',
  [LAST_SEEN_FILTER.YESTERDAY]: 'yesterday',
  [LAST_SEEN_FILTER.LAST_7_DAYS]: 'last7Days',
  [LAST_SEEN_FILTER.LAST_30_DAYS]: 'last30Days',
  [LAST_SEEN_FILTER.LAST_60_DAYS]: 'last60Days',
  [LAST_SEEN_FILTER.ALL_TIME]: 'allTime',
};
